import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Brain, RefreshCw, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SEOCopilot() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [seoActions, setSeoActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [meta, setMeta] = useState({ has_ga4: false, has_gsc: false, total_opportunities: 0, client_name: '' });
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClientId) fetchActions();
  }, [selectedClientId]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients`, { headers });
      const d = await res.json();
      const list = d.clients || d || [];
      setClients(list);
      if (list.length > 0) setSelectedClientId(list[0].id);
    } catch (e) { console.error(e); }
    finally { setLoadingClients(false); }
  };

  const fetchActions = async () => {
    if (!selectedClientId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/seo-copilot/actions?client_id=${selectedClientId}`, { headers });
      const d = await res.json();
      setSeoActions(d.actions || []);
      setMeta({
        has_ga4: d.has_ga4 || false,
        has_gsc: d.has_gsc || false,
        total_opportunities: d.total_opportunities || 0,
        client_name: d.client_name || '',
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const markDone = async (action, status) => {
    try {
      await fetch(`${BACKEND_URL}/api/seo-copilot/mark-done`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: selectedClientId,
          rule_type: action.rule_type,
          entity_type: action.entity_type,
          entity_value: action.entity_value,
          status,
        }),
      });
      fetchActions();
    } catch (e) { alert('Error'); }
  };

  const ruleStyle = {
    ctr_bajo: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', label: 'CTR Bajo' },
    casi_top_3: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', label: 'Casi Top 3' },
    ctr_outlier: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', label: 'Fórmula ganadora' },
    rebote_alto: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', label: 'Rebote alto' },
    trafico_sin_conversion: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', label: 'Sin conversión' },
    posicion_perdida: { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700', label: 'Posición perdida' },
    query_emergente: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', label: 'Query emergente' },
    canal_debil: { bg: 'bg-pink-50', border: 'border-pink-200', badge: 'bg-pink-100 text-pink-700', label: 'Canal en caída' },
    engagement_bajo: { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-700', label: 'Engagement bajo' },
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">SEO Copilot</h1>
                <p className="text-sm text-slate-500">Acciones priorizadas basadas en GA4 + Search Console</p>
              </div>
            </div>
            <button onClick={fetchActions} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>

          {/* Client selector */}
          {!loadingClients && clients.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-500">Cliente:</label>
              <select value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500 bg-white">
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3"></div>
            <p className="text-sm text-slate-500">Analizando datos de {meta.client_name || 'cliente'}...</p>
          </div>
        )}

        {/* Sin clientes */}
        {!loadingClients && clients.length === 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center">
            <div className="text-5xl mb-4">👥</div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay clientes</h3>
            <p className="text-sm text-slate-600">Creá un cliente primero desde la sección Clientes.</p>
          </div>
        )}

        {/* Sin conexiones */}
        {!loading && selectedClientId && !meta.has_ga4 && !meta.has_gsc && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
            <div className="text-5xl mb-4">🔌</div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Conectá GA4 y Search Console para {meta.client_name}</h3>
            <p className="text-sm text-slate-600 mb-4">El Copilot necesita datos de Analytics y Search Console para generar recomendaciones.</p>
            <a href={`/clients/${selectedClientId}`} className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700">
              Ir a Fuentes de datos
            </a>
          </div>
        )}

        {/* Sin oportunidades */}
        {!loading && (meta.has_ga4 || meta.has_gsc) && seoActions.length === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">¡Todo en orden!</h3>
            <p className="text-sm text-slate-600">No detectamos oportunidades críticas. Seguí sincronizando datos y volvé en unos días.</p>
          </div>
        )}

        {/* Lista */}
        {!loading && seoActions.length > 0 && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{seoActions.length}</span> {seoActions.length === 1 ? 'acción prioritaria' : 'acciones prioritarias'}
                {meta.total_opportunities > seoActions.length && (
                  <span className="text-slate-400"> · {meta.total_opportunities} oportunidades totales</span>
                )}
              </p>
            </div>

            <div className="space-y-4">
              {seoActions.map((a, idx) => {
                const s = ruleStyle[a.rule_type] || { bg: 'bg-slate-50', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-700', label: a.rule_type };
                return (
                  <div key={idx} className={`${s.bg} ${s.border} border rounded-2xl p-6`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg font-bold text-slate-400">#{idx + 1}</span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.badge}`}>{s.label}</span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-1">{a.title}</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500 uppercase font-semibold">Impacto</p>
                        <p className="text-sm font-bold text-emerald-600">{a.impact_estimate}</p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Problema</p>
                        <p className="text-sm text-slate-700">{a.problem}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Qué hacer</p>
                        <p className="text-sm text-slate-700">{a.action}</p>
                      </div>
                      {a.example && (
                        <div className="bg-white/70 border border-slate-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">💡 Ejemplo</p>
                          <p className="text-sm text-slate-600 italic">{a.example}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
                      <button onClick={() => markDone(a, 'done')} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                        <Check className="w-4 h-4" /> Marcar como hecha
                      </button>
                      <button onClick={() => markDone(a, 'dismissed')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50">
                        <X className="w-4 h-4" /> Descartar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
